import React, { ChangeEvent, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { BackButton, TextButton as Button, COLORS, ComponentM, ComponentS, ComponentTextStyle, ComponentXS, IconButton, Size, SystemIcons, ToggleButton } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AttachmentDetails, AttachmentHeader, AttachmentsContainer, ChoseFileContainer, GrayBox, StepContainer, StepDescription, StepTitle } from './StepComponents';
import BreadcrumbTopSection from '../../../../commonComponents/BreadcrumbTopSection';
import { selectMarket, buildMarketPath } from '../../../../store/slices/globalConfigSlice';
import posthog from 'posthog-js';

const ProductContainer = styled.div`
    position: relative;
    display: flex;
    min-height: 64px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    color:unset;

    border-radius: 8px;
    border: 1px solid ${COLORS.neutral_200};
    background:${COLORS.white};

    img {
        margin: 0 auto;
    }
`;

const ProductContainerText = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;

    font-family: Lato;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 24px */
`;

const Tog = styled(ToggleButton) <{ fillcolor?: string }>`    
    position: absolute;
    top: 4px;
    right: 4px;
    & > button > div > svg > path {
        fill: ${({ fillcolor }) => fillcolor || 'unset'} !important;
    }
`;


interface Step6Props {
  onNext: () => void;
  onPrevious: () => void;
  handleResolvedStep: () => void;
  setSelectedProducts: (products: string[]) => void;
  setAttachedFiles: (files: File[]) => void;
}

const Step6: React.FC<Step6Props> = ({ onNext, onPrevious, handleResolvedStep, setSelectedProducts, setAttachedFiles }) => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const MAX_FILE_SIZE = 21 * 1024 * 1024;

  const [products, setProducts] = useState([
    { name: 'Little Anne QCPR', imagePath: '/assets/qcprSupport-LittleAnneQCPR.png', active: false },
    { name: 'Little Junior QCPR', imagePath: '/assets/qcprSupport-LittleJuniorQCPR.jpg', active: false },
    { name: 'Little Anne Stackable', imagePath: '/assets/qcprSupport-LittleAnneStackable.png', active: false },
    { name: 'Little Baby QCPR', imagePath: '/assets/qcprSupport-LittleBabyQCPR.jpg', active: false },
    { name: 'Resusci Anne QCPR', imagePath: '/assets/qcprSupport-ResusciAnneQCPR.jpg', active: false },
    { name: 'Resusci Baby QCPR', imagePath: '/assets/qcprSupport-ResusciBabyQCPR.png', active: false },
    { name: 'AED Trainer ', imagePath: '/assets/qcprSupport-AEDTrainer.png', active: false },
  ]);
  const [files, setFiles] = useState<File[]>([]);
  const [fileSizeOverLimit, setFileSizeOverLimit] = useState<boolean>(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };
  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles) {
      setFiles(prevFiles => [...prevFiles, ...Array.from(newFiles)]);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the current file input
      }
    }
  }
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };


  const toggleProduct = (index: number) => {
    setProducts(prev => prev.map((product, i) =>
      i === index ? { ...product, active: !product.active } : product
    ));
  };

  useEffect(() => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    setFileSizeOverLimit(totalSize > MAX_FILE_SIZE);
  }, [files]);

  return (
    <StepContainer>
      <BreadcrumbTopSection
        setPadding={false}
        breadcrumb={[
          { text: t('Home', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/') },
          { text: t('Software and Apps', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps') },
          { text: t('QCPR App', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps/product/qcpr-app') },
          { text: t('Troubleshooting', { ns: 'qcprSupport' }) }
        ]}
      />

      <BackButton onClick={onPrevious} size={Size.Small}>
        {t('Back', { ns: 'qcprSupport' })}
      </BackButton>

      <StepTitle>{t('Which manikins are you experiencing issue with?', { ns: 'qcprSupport' })}</StepTitle>
      <StepDescription>{t('Connectivity depends on type of Laerdal device.', { ns: 'qcprSupport' })}</StepDescription>

      <GrayBox>
        <ComponentM textStyle={ComponentTextStyle.Regular}>
          {t('You can establish up to 6 bluetooth connections simultaneously.', { ns: 'qcprSupport' })}
        </ComponentM>
      </GrayBox>

      {products.map((product, index) => (
        <ProductContainer key={index} onClick={() => toggleProduct(index)}>
          <Tog
            onChange={(value) => toggleProduct(index)}
            fillcolor={product.active ? COLORS.primary_500 : COLORS.neutral_600}
            active={product.active}
            color={COLORS.primary_500}
            defaultState={{ icon: <SystemIcons.CheckboxOff size='32px' color={COLORS.positive_500} /> }}
            activeState={{ icon: <SystemIcons.CheckboxOn size='24px' color={COLORS.primary_500} /> }}
          />
          <img src={product.imagePath} style={{ maxWidth: '100%', height: '160px' }} alt={product.name} />
          <ProductContainerText>
            {product.name}
          </ProductContainerText>
        </ProductContainer>
      ))}

      <ChoseFileContainer
        onClick={handleClick}>

        <ComponentM textStyle={ComponentTextStyle.Bold} color={COLORS.neutral_500}>{t('Add the image of the Laerdal device', { ns: 'qcprSupport' })}</ComponentM>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          multiple
          style={{ display: 'none' }}
        />

        <Button icon={<SystemIcons.Upload />} onClick={handleButtonClick} variant="secondary">{t('Add a photo', { ns: 'qcprSupport' })}</Button>
      </ChoseFileContainer>

      {files.length > 0 && (
        <AttachmentsContainer>
          <AttachmentHeader>
            <ComponentXS textStyle={ComponentTextStyle.Bold}>{t('Images', { ns: 'qcprSupport' })} </ComponentXS>
          </AttachmentHeader>
          {fileSizeOverLimit &&
            <ComponentM color={COLORS.critical_500}>
              {t('Files too large! Maximum is 20MB', { ns: 'qcprSupport' })}
            </ComponentM>
          }

          <>
            {files.map((file, index) => (
              <AttachmentDetails key={index}>
                <IconButton variant="secondary" action={() => handleRemoveFile(index)}>
                  <SystemIcons.Close color={COLORS.neutral_500} />
                </IconButton>

                <ComponentS color={COLORS.neutral_500} >{file.name}</ComponentS>
              </AttachmentDetails>
            ))}
          </>
        </AttachmentsContainer>
      )}

      <Button
        size={Size.Large}
        variant='secondary'
        onClick={() => {
          // Get active product names
          const activeProductNames = products
            .filter(product => product.active)
            .map(product => product.name);

          posthog.capture('SUPP-QcprSupport-ProductsSelected', {
            step: 6,
            selected_products: activeProductNames,
            files_attached: files.length
          });

          // Pass the data to parent
          setSelectedProducts(activeProductNames);
          setAttachedFiles(files);

          onNext();
        }}>
        {t("Continue", { ns: 'qcprSupport' })}
      </Button>

      <StepDescription>{t('Your answers will help our team to assist you.', { ns: 'qcprSupport' })}</StepDescription>
    </StepContainer>
  );
};

export default Step6;
